<script lang="ts">
  /**
   * The switcher shown in preview mode: Cut, Fade (time and style) and a T-bar take the preview
   * live. The main window shows the preview; live is on the outputs and in the corner monitor.
   */
  import type { AppController } from '../../app/controller.svelte';
  import type { TransitionType } from '../../shared/scene';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();

  const TIMES = [0.5, 1, 2, 3, 5, 10];
  const STYLES: { value: Exclude<TransitionType, 'cut'>; label: string }[] = [
    { value: 'crossfade', label: 'Fade' },
    { value: 'wipe', label: 'Wipe' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'luma', label: 'Luma' },
    { value: 'morph', label: 'Morph' },
  ];

  const newest = $derived(ctl.takes.at(-1));
  /** The T-bar is holding a take part-way: Fade carries it on from there. */
  const resumable = $derived(!!newest && !newest.auto);
  const tbar = $derived(ctl.tbarValue);
</script>

<div class="switcher" role="group" aria-label="Take the preview live" data-testid="switcher">
  <button
    class="cut"
    data-testid="take-cut"
    title="Cut to live now (Shift+Space or Shift+Enter)"
    onclick={() => ctl.cut()}>Cut</button
  >
  <button
    class="primary"
    data-testid="take-fade"
    title={resumable
      ? 'Carry on fading from where the T-bar is (Space)'
      : 'Fade to live (Space or Enter); fades can overlap'}
    onclick={() => ctl.fade()}>{resumable ? 'Resume' : 'Fade'}</button
  >
  <select
    aria-label="Fade time"
    value={String(ctl.takeStyle.seconds)}
    onchange={(e) => (ctl.takeStyle = { ...ctl.takeStyle, seconds: Number(e.currentTarget.value) })}
  >
    {#each TIMES as t (t)}<option value={String(t)}>{t} s</option>{/each}
  </select>
  <select
    aria-label="Transition style"
    value={ctl.takeStyle.type}
    onchange={(e) =>
      (ctl.takeStyle = {
        ...ctl.takeStyle,
        type: e.currentTarget.value as Exclude<TransitionType, 'cut'>,
      })}
  >
    {#each STYLES as s (s.value)}<option value={s.value}>{s.label}</option>{/each}
  </select>
  <label
    class="tbar"
    title="Drag to mix from live to the preview. Grab it mid-fade to take over; let go at the top and it's live."
  >
    <span class="small muted">T-bar</span>
    <input
      type="range"
      min="0"
      max="1000"
      step="1"
      value={Math.round(tbar * 1000)}
      class:running={!!newest?.auto}
      aria-label="T-bar: mix from live to preview"
      data-testid="tbar"
      onpointerdown={() => ctl.tbarGrab()}
      oninput={(e) => ctl.tbarMove(Number(e.currentTarget.value) / 1000)}
      onchange={() => ctl.tbarRelease()}
      onpointerup={() => ctl.tbarRelease()}
      onpointerleave={() => ctl.tbarRelease()}
    />
  </label>
  <button
    class="icon-btn"
    aria-label="Leave preview mode"
    title={`Leave preview mode${ctl.keyHint('preview.toggle')}`}
    onclick={() => ctl.setPreviewMode(false)}><Icon name="close" /></button
  >
</div>

<style>
  .switcher {
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: var(--radius);
    background: rgba(10, 10, 16, 0.88);
    border: 1px solid var(--border);
    backdrop-filter: blur(10px);
    max-width: calc(100vw - 32px);
    flex-wrap: wrap;
  }
  .cut {
    border-color: var(--danger);
    color: var(--danger);
    font-weight: 600;
  }
  select {
    font-size: 12px;
  }
  .tbar {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .tbar input {
    width: 160px;
    accent-color: var(--danger);
  }
  /* Green while a fade is running on its own; red while you hold it. */
  .tbar input.running {
    accent-color: var(--ok);
  }
</style>
